import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import { useProfile } from '../context/ProfileContext';
import { callOpenAI } from '../openaiClient';

const { FiUploadCloud, FiCpu, FiCheck, FiX, FiEdit3, FiDownload, FiChevronRight, FiAlertCircle, FiZap, FiTarget, FiExternalLink, FiBookOpen, FiTrendingUp } = FiIcons;

const Optimizer = () => {
  const { data } = useProfile();
  const baseUserResume = data.user;
  const skillGapRecommendations = data.skillGaps;

  const [jobDescription, setJobDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);

  const handleAnalyze = async () => {
    if (!jobDescription) return;
    setIsAnalyzing(true);
    
    try {
      const userSkills = baseUserResume?.skills || [];
      const userBullets = baseUserResume?.experience?.[0]?.bullets || [];
      const firstBullet = userBullets.length > 0 ? userBullets[0] : "I worked on various projects.";

      const systemPrompt = `You are an expert ATS (Applicant Tracking System) optimizer and career coach.
You must return a JSON object with the following exact structure:
{
  "score": integer (0-100),
  "missingSkills": [array of up to 5 strings of skills in the job desc but missing from user profile],
  "matchedSkills": [array of strings of skills in both],
  "bulletSuggestions": [
    {
      "original": "string",
      "suggested": "string (optimized with action verbs and metrics)",
      "impact": "string (short description of why this is better)"
    }
  ],
  "chartData": {
    "categories": ["Strategy", "Execution", "Tools", "Analytics", "Soft Skills", "Leadership"],
    "userScore": [array of 6 integers 0-100],
    "jobReq": [array of 6 integers 0-100]
  }
}
Do not return anything else.`;

      const userPrompt = `Job Description:
${jobDescription}

User Profile Skills:
${userSkills.join(', ')}

User Resume Bullet to Optimize:
${firstBullet}`;

      const aiResponse = await callOpenAI(systemPrompt, userPrompt);

      setResults(aiResponse);
    } catch (error) {
      console.error("Analysis failed:", error);
      // Fallback if API fails
      setResults({
        score: 0,
        missingSkills: ["Error analyzing"],
        matchedSkills: [],
        bulletSuggestions: [{ original: "", suggested: "Please check your OpenAI API key.", impact: "" }],
        chartData: {
          categories: ['Strategy', 'Execution', 'Tools', 'Analytics', 'Soft Skills', 'Leadership'],
          userScore: [0, 0, 0, 0, 0, 0],
          jobReq: [0, 0, 0, 0, 0, 0]
        }
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getChartOption = (chartData) => ({
    radar: {
      indicator: chartData.categories.map(name => ({ name, max: 100 })),
      splitArea: { show: false },
      axisLine: { lineStyle: { color: '#e5e7eb' } }
    },
    series: [{
      type: 'radar',
      data: [
        {
          value: chartData.userScore,
          name: 'Your Profile',
          areaStyle: { color: 'rgba(59, 130, 246, 0.4)' },
          lineStyle: { color: '#3b82f6', width: 2 }
        },
        {
          value: chartData.jobReq,
          name: 'Job Requirement',
          areaStyle: { color: 'rgba(156, 163, 175, 0.1)' },
          lineStyle: { color: '#9ca3af', width: 2, type: 'dashed' }
        }
      ]
    }],
    legend: { bottom: 0, data: ['Your Profile', 'Job Requirement'] }
  });

  return (
    <div className="p-8 max-w-7xl mx-auto h-[calc(100vh-80px)] overflow-hidden flex flex-col">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">ATS Optimizer Pro</h1>
          <p className="text-gray-500 mt-1">Deep analysis with skill gap recommendations & learning paths.</p>
        </div>
      </div>

      <div className="flex gap-6 flex-1 min-h-0">
        <div className="w-[35%] flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
            <h2 className="font-semibold text-gray-700 flex items-center gap-2">
              <SafeIcon icon={FiUploadCloud} /> Job Description
            </h2>
          </div>
          <textarea 
            className="flex-1 w-full p-6 resize-none focus:outline-none text-gray-700 leading-relaxed text-sm"
            placeholder="Paste the job description here..."
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
          />
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <button 
              onClick={handleAnalyze}
              disabled={isAnalyzing || !jobDescription}
              className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
                isAnalyzing || !jobDescription 
                  ? 'bg-gray-100 text-gray-400' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30'
              }`}
            >
              {isAnalyzing ? <SafeIcon icon={FiCpu} className="animate-spin" /> : <SafeIcon icon={FiZap} />}
              {isAnalyzing ? 'Analyzing Alignment...' : 'Analyze & Suggest'}
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden relative">
          <AnimatePresence mode="wait">
            {!results && !isAnalyzing && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 p-12 text-center"
              >
                <SafeIcon icon={FiTarget} className="text-6xl opacity-10 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600">Enter Job Details</h3>
                <p className="max-w-xs mx-auto mt-2 text-sm">We'll identify skill gaps, provide tailored learning resources, and optimize your resume bullets.</p>
              </motion.div>
            )}

            {results && !isAnalyzing && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-900 text-white p-8 rounded-2xl shadow-xl flex flex-col justify-center">
                    <p className="text-blue-400 text-xs font-black uppercase tracking-widest mb-2">ATS Match Score</p>
                    <h2 className="text-7xl font-black">{results.score}%</h2>
                    <div className="mt-6 flex items-center gap-2 text-sm text-gray-400">
                      <SafeIcon icon={FiAlertCircle} className="text-orange-400" />
                      Critical match for target requirements
                    </div>
                  </div>
                  
                  <div className="bg-white border border-gray-100 p-4 rounded-2xl shadow-sm h-[250px]">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 text-center">Skill Alignment</h4>
                    <ReactECharts option={getChartOption(results.chartData)} style={{ height: '180px' }} />
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                      <SafeIcon icon={FiTrendingUp} className="text-blue-500" /> Skill Gap Tracker
                    </h3>
                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      {skillGapRecommendations.filter(s => s.status === 'Completed').length} / {skillGapRecommendations.length} Resolved
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {skillGapRecommendations.map(rec => (
                      <div key={rec.id} className="p-6 rounded-3xl bg-gray-50 border border-gray-100 hover:border-blue-200 transition-all group">
                        <div className="flex justify-between items-start mb-6">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                                rec.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'
                              }`}>
                                {rec.status}
                              </span>
                              <span className="text-[10px] font-black text-orange-600 uppercase">High Priority</span>
                            </div>
                            <h4 className="font-black text-gray-900 text-lg uppercase tracking-tight">{rec.skill}</h4>
                            <p className="text-sm text-gray-600 mt-1">{rec.reason}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-black text-gray-900">{rec.progress}%</span>
                            <p className="text-[10px] font-black text-gray-400 uppercase">Mastery</p>
                          </div>
                        </div>

                        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden mb-6">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${rec.progress}%` }}
                            className="bg-blue-600 h-full"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {rec.resources.map((res, i) => (
                            <a key={i} href={res.url || '#'} className="group/link flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl hover:border-blue-500 transition-all shadow-sm">
                              <div>
                                <p className="text-[9px] text-gray-400 font-black uppercase mb-0.5">{res.provider} • Course</p>
                                <p className="text-xs font-black text-gray-800 group-hover/link:text-blue-600">{res.name}</p>
                              </div>
                              <SafeIcon icon={FiExternalLink} className="text-gray-300 group-hover/link:text-blue-500" />
                            </a>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                    <SafeIcon icon={FiEdit3} className="text-purple-500" /> AI Bullet Transformations
                  </h3>
                  {results.bulletSuggestions.map((sug, i) => (
                    <div key={i} className="bg-purple-50 border border-purple-100 rounded-3xl p-8">
                      <div className="mb-4">
                        <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Original</p>
                        <p className="text-sm text-gray-600 italic">"{sug.original}"</p>
                      </div>
                      <div className="mb-4">
                        <p className="text-[10px] font-black text-purple-600 uppercase mb-1">AI Optimized</p>
                        <p className="text-xl font-black text-purple-900 leading-tight">"{sug.suggested}"</p>
                      </div>
                      <div className="flex items-center gap-3 pt-6 border-t border-purple-100/50">
                        <div className="p-1.5 bg-green-100 rounded-full">
                          <SafeIcon icon={FiCheck} className="text-green-600 text-xs" />
                        </div>
                        <span className="text-xs font-black text-purple-700 uppercase">{sug.impact}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Optimizer;